/**
 * iter-232 Phase 9 — Multi-vendor partial reversal dialog.
 *
 * Used in two contexts:
 *   1. From RefundModal when Razorpay returns `multi_vendor_partial` —
 *      the broad `reverse_all` refund couldn't run; admin must
 *      reverse each vendor's slice manually.
 *   2. Standalone on the order detail page (admin section) when admin
 *      knows up-front that the order is multi-vendor and wants to
 *      claw back a single vendor's share.
 *
 * Calls POST /api/admin/razorpay/refunds/multi-vendor-partial-reversal
 * which is backed by services.razorpay_route_phase9_11.recover_via_transfer_reversal.
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Loader2, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const MultiVendorReversalDialog = ({ order, open, onOpenChange, onDone }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  // Build candidate vendor list from the order's items / vendor_breakdowns
  const vendors = useMemo(() => {
    if (!order) return [];
    if (Array.isArray(order.vendor_breakdowns) && order.vendor_breakdowns.length) {
      return order.vendor_breakdowns.map((vb) => ({
        vendor_id: vb.vendor_id || vb.id,
        label: vb.vendor_name || vb.vendor_id || 'Unknown vendor',
        max_paise: Math.round((vb.subtotal || vb.total || vb.amount || 0) * 100),
      }));
    }
    // Fallback: derive from line items
    const groups = {};
    (order.items || order.line_items || []).forEach((it) => {
      const vid = it.vendor_id || it.vendor || 'unknown';
      if (!groups[vid]) {
        groups[vid] = { vendor_id: vid, label: it.vendor_name || vid, max_paise: 0 };
      }
      const amt = Math.round((it.price || it.total || 0) * (it.quantity || 1) * 100);
      groups[vid].max_paise += amt;
    });
    return Object.values(groups);
  }, [order]);

  const [vendorId, setVendorId] = useState('');
  const [amountInr, setAmountInr] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setVendorId('');
    setAmountInr('');
    setResult(null);
  };

  const handleClose = (next) => {
    if (!running) {
      if (!next) reset();
      onOpenChange(next);
    }
  };

  const handleRecover = async () => {
    if (!vendorId) {
      toast({ title: 'Pick a vendor', variant: 'destructive' });
      return;
    }
    const paise = Math.round(parseFloat(amountInr || '0') * 100);
    if (!paise || paise <= 0) {
      toast({ title: 'Amount must be > ₹0', variant: 'destructive' });
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await apiCall(
        '/api/admin/razorpay/refunds/multi-vendor-partial-reversal',
        {
          method: 'POST',
          body: JSON.stringify({
            order_id: order?.id,
            vendor_id: vendorId,
            amount_paise: paise,
            razorpay_payment_id: order?.razorpay_payment_id || null,
            razorpay_order_id: order?.razorpay_order_id || null,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      );
      setResult(res);
      if (res?.ok) {
        toast({
          title: 'Reversal initiated',
          description: `rvrsl ${res.reversal_id} on transfer ${res.transfer_id}`,
        });
        if (typeof onDone === 'function') onDone(res);
      } else {
        toast({
          title: 'Reversal failed',
          description: res?.error || res?.error_type || 'See panel.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      const msg = err?.message || 'Network error';
      setResult({ ok: false, error: msg });
      toast({ title: 'Reversal failed', description: msg, variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="multi-vendor-reversal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            Recover from one vendor (Transfer Reversal)
          </DialogTitle>
        </DialogHeader>

        <Alert className="my-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Per-vendor reversal</AlertTitle>
          <AlertDescription className="text-xs">
            Razorpay can&apos;t auto-decide which transfer to partially reverse on
            multi-vendor orders. Pick the vendor whose payout you want to claw
            back and the exact INR amount. The amount is debited from the
            vendor&apos;s Linked Account balance and credited to your platform balance.
            If the vendor has already withdrawn, Razorpay will reject — that case
            requires manual collection.
          </AlertDescription>
        </Alert>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="mvr-vendor">Vendor</Label>
            <select
              id="mvr-vendor"
              data-testid="mvr-vendor-select"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              className="w-full border rounded h-9 px-2 text-sm"
              disabled={running}
            >
              <option value="">— Select vendor —</option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.label}{v.max_paise ? ` (max ₹${(v.max_paise / 100).toFixed(2)})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mvr-amount">Amount (INR)</Label>
            <Input
              id="mvr-amount"
              data-testid="mvr-amount-input"
              type="number"
              step="0.01"
              min="0.01"
              value={amountInr}
              onChange={(e) => setAmountInr(e.target.value)}
              placeholder="0.00"
              disabled={running}
            />
          </div>
        </div>

        {result && (
          <Alert
            variant={result.ok ? 'default' : 'destructive'}
            data-testid="mvr-result"
            className="mt-2"
          >
            {result.ok
              ? <CheckCircle2 className="h-4 w-4" />
              : <AlertTriangle className="h-4 w-4" />}
            <AlertTitle>
              {result.ok ? 'Reversal succeeded' : `Reversal failed${result.error_type ? ` · ${result.error_type}` : ''}`}
            </AlertTitle>
            <AlertDescription className="text-xs">
              {result.ok ? (
                <div className="font-mono">
                  reversal_id: {result.reversal_id}<br />
                  transfer_id: {result.transfer_id}<br />
                  amount: ₹{((result.amount_paise || 0) / 100).toFixed(2)}
                </div>
              ) : (
                result.error || 'See network console.'
              )}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={running} data-testid="mvr-cancel">
            {result?.ok ? 'Close' : 'Cancel'}
          </Button>
          {!result?.ok && (
            <Button onClick={handleRecover} disabled={running} data-testid="mvr-recover-btn">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reverse ₹{amountInr || '0.00'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MultiVendorReversalDialog;
